import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import Lightbox from '@athena/spark/Lightbox';
import Textarea from '@athena/spark/Textarea';
import InlineAlert from '@athena/spark/InlineAlert';
import Button from '@athena/spark/Button';
import Multiselect from '@athena/spark/Multiselect';
import { useTranslation } from 'react-i18next';
import Banner from '@athena/spark/Banner';
import BannerItem from '@athena/spark/BannerItem';
import { CSSTransition } from 'react-transition-group';
import { appInsights } from 'src/shared/lib/appInsights';
import { useAmplitude } from 'src/lib/amplitude';
import { useAppState } from 'src/components/provider/AppStateProvider';
import useIsMobile from 'src/lib/hooks/useIsMobile';
import Footer from 'src/components/ui/Footer';
import styles from './ReportIssueLightbox.module.scss';

const ReportIssueLightbox: React.FC = () => {
  const { t } = useTranslation('TECHNICAL_ISSUE');
  const [selectedIssues, setSelectedIssues] = useState<Array<string>>([]);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const isMobile = useIsMobile();
  const { ampTrackEvent, ampTrackPageView } = useAmplitude('ReportIssue');
  const [showBanner, setShowBanner] = useState(false); // opens React portal to append banner to MainLayout
  const [showTransition, setShowTransition] = useState(false); // activates transition animation when opening and closing banner
  const formIsValid = selectedIssues.length > 0;
  const [reportIssueTypeError, setReportIssueTypeError] = useState('');
  const { setAppState, showReportIssueLightbox } = useAppState();

  const options = [
    { value: 'I couldn’t see anyone', text: t('REPORT_ISSUE_TYPE_1') },
    { value: 'I couldn’t hear anyone', text: t('REPORT_ISSUE_TYPE_2') },
    { value: 'Image quality was poor', text: t('REPORT_ISSUE_TYPE_3') },
    { value: 'Sound quality was poor', text: t('REPORT_ISSUE_TYPE_4') },
    { value: 'People had trouble joining the call', text: t('REPORT_ISSUE_TYPE_5') },
    { value: 'Other', text: t('REPORT_ISSUE_TYPE_6') },
  ];

  const closeModal = () => {
    setAdditionalInfo('');
    setSelectedIssues([]);
    setAppState({ showReportIssueLightbox: false });
  };

  const handleOnSubmit = async () => {
    if (!formIsValid) {
      setReportIssueTypeError(selectedIssues.length === 0 ? 'REPORT_ISSUE_TYPE_ERROR' : '');
      return;
    }

    // send custom event to App Insights. note the additional
    // identifiers(contextId, etc) are not included in the payload
    // here as they are automatically included in the telemetry initializer
    appInsights.trackEvent({ name: 'sendFeedback' }, { issues: selectedIssues.join(' | '), additionalInfo });

    ampTrackEvent({
      action: 'SendFeedback',
      data: {
        'ReportIssue::IssueType': selectedIssues.join(' | '),
        'ReportIssue::PleaseExplain': additionalInfo,
      },
    });

    setShowBanner(true);
    setTimeout(() => {
      setShowTransition(true), 200;
    });
    setTimeout(() => setShowTransition(false), 4000);
    closeModal();
  };

  const handleOnClose = async () => {
    ampTrackEvent({ action: 'Close' });
    closeModal();
  };

  useEffect(() => {
    if (showReportIssueLightbox) {
      ampTrackPageView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReportIssueLightbox]);

  const bannerComponent = (
    <CSSTransition in={showTransition} timeout={300} classNames={{ ...styles }} unmountOnExit>
      <div role="alert">
        <Banner alertType="success">
          <BannerItem headerText={t('REPORT_ISSUE_FEEDBACK_SENT')}>{t('REPORT_ISSUE_FEEDBACK_THANK_YOU')}</BannerItem>
        </Banner>
      </div>
    </CSSTransition>
  );

  return (
    <>
      <Lightbox
        aria-label={t('REPORT_ISSUE_HEADING')}
        header={t('REPORT_ISSUE_HEADING')}
        show={showReportIssueLightbox}
        onHide={handleOnClose}
        data-test-id="ReportIssueLightbox"
      >
        <div className={styles.wrapper}>
          <div>
            <label htmlFor="issue-type" className={styles.label}>
              {t('REPORT_ISSUE_TYPE_LABEL')} *
            </label>
            <Multiselect
              placeholder={t('REPORT_ISSUE_TYPE_PLACEHOLDER')}
              inputId="issue-type"
              onChange={(e: { target: { value?: Array<{ value: string; text?: string }> } }) => {
                setSelectedIssues(e.target.value?.map((opt) => opt.value) || []);
                if (e.target.value) {
                  setReportIssueTypeError('');
                }
              }}
              className={cx(
                { [styles.multiSelectMobile]: isMobile },
                'fe_u_margin--top-small',
                'fe_u_margin--bottom-large'
              )}
              aria-label={t('REPORT_ISSUE_TYPE_LABEL')}
              options={options}
              error={reportIssueTypeError}
            />
            {reportIssueTypeError && (
              <Banner className={`${styles.banner}`} alertType="attention">
                <BannerItem className={styles.bannerItem}>
                  <p data-testid="alert-banner-name">{t('REPORT_ISSUE_TYPE_ERROR')}</p>
                  <div id="hiddenInputAlertContainer" role="alert" className={styles.visuallyHidden} />
                </BannerItem>
              </Banner>
            )}
            <label htmlFor="additional information">
              <span className={styles.label}>{t('REPORT_ISSUE_EXPLANATION_LABEL_1')}</span>{' '}
              <span className="fe_u_font-size--large">{t('REPORT_ISSUE_EXPLANATION_LABEL_2')}</span>
            </label>
            <Textarea
              data-testid="additional-info"
              name="additional information"
              aria-label={t('REPORT_ISSUE_EXPLANATION_PLACEHOLDER')}
              value={additionalInfo}
              id="additional information"
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder={t('REPORT_ISSUE_EXPLANATION_PLACEHOLDER')}
              className={styles.textBox}
            />
            <InlineAlert className="fe_u_margin--top-small" type="info">
              {t('REPORT_ISSUE_NOTIFICATION')}
            </InlineAlert>
            {!isMobile ? (
              <div className={styles.desktopBtnWrapper}>
                <Button
                  data-testid="feedback-btn"
                  onClick={handleOnSubmit}
                  className={styles.desktopBtn}
                  variant="solid-dark"
                >
                  {t('REPORT_ISSUE_SEND_FEEDBACK_BUTTON')}
                </Button>
                <Button
                  data-testid="close-btn"
                  onClick={handleOnClose}
                  className={cx(styles.desktopBtn, 'fe_u_margin--left-medium')}
                  variant="outline"
                >
                  {t('REPORT_ISSUE_SEND_CLOSE_BUTTON')}
                </Button>
              </div>
            ) : null}
          </div>
          <div className="fe_u_margin--top-large">
            {isMobile ? (
              <>
                <Button onClick={handleOnSubmit} variant="solid-dark" fullWidth size="large">
                  {t('REPORT_ISSUE_SEND_FEEDBACK_BUTTON')}
                </Button>
                <Button
                  data-testid="close-btn"
                  onClick={handleOnClose}
                  className="fe_u_margin--top-small"
                  variant="outline"
                  fullWidth
                  size="large"
                >
                  {t('REPORT_ISSUE_SEND_CLOSE_BUTTON')}
                </Button>
              </>
            ) : null}
            <Footer />
          </div>
        </div>
      </Lightbox>
      {showBanner && createPortal(bannerComponent, document.getElementById('banner-container') as HTMLElement)}
    </>
  );
};

export default ReportIssueLightbox;
